import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  atxIsMobile$,
  atxIsTouchScreen,
} from '@epicuro-next/ataraxia/breakpoint';
import { ListProductReference } from '@epicuro-next/data/lists';
import { isNonNullish } from '@epicuro-next/utilities';
import {
  FloatRange,
  Product,
  ProductMedia,
  ProductPriceRanges,
} from 'graphql-api/gql-api-interfaces';
import { map, shareReplay } from 'rxjs/operators';

import { ProductCardType } from '../../interfaces/product-card-type';

export interface ProductCardBaseComponentInputs {
  deletable: boolean;
  dragAndDropHandleTemplate?: TemplateRef<unknown> | null;
  enableProgressiveImageLoad: boolean;
  hideMarkupPriceValue: boolean;
  isDraggable: boolean;
  prices: ProductPriceRanges[];
  product: Product;
  productReference: ListProductReference;
  showActions: boolean;
  showAsUnavailable: boolean;
  showBookmarkIcon: boolean;
  showCommercialListDate: boolean;
  showIconsOnHoverRule: boolean;
  showMarkupValue: boolean;
  showPopularityIcon: boolean;
  showUnavailableDeleteButton: boolean;
  showVariations: boolean;
  type: ProductCardType;
}

@Component({
  selector: 'atx-product-card-base',
  templateUrl: 'product-card-base.component.html',
  styleUrls: ['product-card-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardBaseComponent
  implements ProductCardBaseComponentInputs
{
  public productCardType = ProductCardType;

  /**
   * Product data
   * TODO: fix the types of the product and the prices after having the graphql API in place
   */
  @Input() public product: Product;

  @Input() public productReference: ListProductReference;
  @Input() public showCommercialListDate = false;
  @Input() public showActions = true;
  @Input() public showBookmarkIcon = true;
  @Input() public deletable = false;
  @Input() public prices: ProductPriceRanges[];

  /**
   * Template to render inside drag & drop container
   */
  @Input() public dragAndDropHandleTemplate: TemplateRef<unknown> | null = null;

  @HostBinding('class.unavailable')
  @Input()
  public showAsUnavailable = false;

  @Input() public showUnavailableDeleteButton = false;
  @Input() public type = ProductCardType.Default;
  @Input() public isDraggable = false;
  @Input() public enableProgressiveImageLoad = true;
  @Input() public showMarkupValue = false;
  @Input() public hideMarkupPriceValue = false;
  @Input() public showPopularityIcon = false;
  @Input() public showVariations = false;
  @Input() public showIconsOnHoverRule = true;
  @Output() public cardClick = new EventEmitter<string>();

  /**
   * Discount max value
   */
  public discount: FloatRange = {};

  public readonly isMobile$ = atxIsMobile$;
  public currentVariantMedia: ProductMedia[] = [];

  public readonly showIconsOnHover$ = this.isMobile$.pipe(
    map((isMobile) => {
      if (atxIsTouchScreen || isMobile) return false;
      return this.showIconsOnHoverRule;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public get showMarkup(): boolean {
    return this.showMarkupValue && !this.hideMarkupPriceValue;
  }

  public get discountVisible() {
    const productPrices = (this.prices || [])[0];

    if (isNonNullish(productPrices?.discount)) {
      this.discount = {
        max: productPrices.discount.max ?? 0,
        min: 0,
      };
    }

    return (this.discount?.max ?? 0) > 0;
  }

  public clickCard() {
    this.cardClick.emit(this.product.productId);
  }
}
